/**
 * Task Suppression (ENG-1711)
 *
 * Some action types are still emitted by the backend `needs_action` view but are
 * no longer surfaced as tasks in My Tasks.
 *
 * The suppression lives on the client, in My Tasks only, on purpose: the same
 * `/needs-action` endpoint also powers the My Referrals "Needs Action" tab and
 * its badge counts, so excluding these rows server-side would silently remove
 * cases from that surface too.
 */

import type {
  ActionType,
  PaginationMetadata,
  SectionData,
  SectionState,
  TaskEntry,
} from '../types'

/**
 * Action types hidden from My Tasks for both buyers and sellers.
 *
 * - `upload_fee_agreement`: the "Upload Fee Agreement" card, along with its
 *   "Upload Fee Agreement" / "Fee Agreement in Place" buttons (ENG-1711).
 *   Fee agreements are still uploaded from the case Data Room sidebar.
 */
export const SUPPRESSED_ACTION_TYPES: readonly ActionType[] = [
  'upload_fee_agreement',
]

/**
 * Withhold Client Contact's flag-gated handoff task: the buyer's "Awaiting
 * Contact Info". The backend emits it whenever a match is withheld -- which
 * Direct Share or Litify can cause with the web flag off -- so it is hidden
 * until the rollout flag is on.
 *
 * The seller's "Share Contact Info" (`share_contact_info`) is deliberately
 * NOT here, nor anywhere else in this file: it is never suppressed. Masking
 * follows the data, not the flag, so the seller's way out of a withheld
 * match must too. Hiding it with the flag off (kill switch, rollback, cohort
 * change) would strand every withheld match -- buyer masked for good, Client
 * Contacted refused, escrow never started.
 */
export const FLAG_GATED_WITHHOLD_CLIENT_CONTACT_ACTION_TYPES: readonly ActionType[] =
  ['awaiting_contact_info']

/** Rollout state the suppression depends on, read once by the caller. */
export interface SuppressionOptions {
  /** `FeatureFlag.WITHHOLD_CLIENT_CONTACT_ENABLED` */
  withholdClientContactEnabled: boolean
}

/** Every action type hidden from My Tasks under the given rollout state. */
export const getSuppressedActionTypes = ({
  withholdClientContactEnabled,
}: SuppressionOptions): readonly ActionType[] =>
  withholdClientContactEnabled
    ? SUPPRESSED_ACTION_TYPES
    : [
        ...SUPPRESSED_ACTION_TYPES,
        ...FLAG_GATED_WITHHOLD_CLIENT_CONTACT_ACTION_TYPES,
      ]

/**
 * Whether a task should be hidden from My Tasks.
 *
 * Tasks with no action type are kept — the backend falls back to a default
 * action for those, so treating them as suppressed would drop real work.
 */
export const isSuppressedTask = (
  task: TaskEntry,
  options: SuppressionOptions
): boolean => {
  const actionType = task.urgency_metadata?.action_type
  return !!actionType && getSuppressedActionTypes(options).includes(actionType)
}

/**
 * Split a fetched page into the tasks to display and a count of those removed.
 *
 * The caller is responsible for accumulating `removedCount` across pages — see
 * `withSuppressedTotal`.
 */
export const partitionSuppressedTasks = (
  items: TaskEntry[],
  options: SuppressionOptions
): { visibleItems: TaskEntry[]; removedCount: number } => {
  const visibleItems = items.filter(task => !isSuppressedTask(task, options))
  return { visibleItems, removedCount: items.length - visibleItems.length }
}

/**
 * Reduce a section's server-reported total by the tasks suppressed so far.
 *
 * `totalEntries` drives the section headers and the urgent alert. `suppressedCount`
 * must be the *cumulative* number removed across every page fetched for the
 * section, not just the latest one: the server repeats the same `totalEntries`
 * on every page, so applying only the newest page's count would discard earlier
 * corrections and let the displayed total climb back up as the user pages.
 *
 * This is exact whenever the whole section fits in the prefetch window (30 urgent
 * / 20 next up), which is the normal case; beyond that it stays an upper bound,
 * since suppressed tasks on pages not yet fetched cannot be counted.
 *
 * `totalPages` and `hasNextPage` are deliberately passed through untouched.
 * Server-side pages still contain the suppressed rows, so shrinking them here
 * would strand pages that really do exist and break "load more".
 */
export const withSuppressedTotal = (
  pagination: PaginationMetadata,
  suppressedCount: number
): PaginationMetadata =>
  suppressedCount <= 0
    ? pagination
    : {
        ...pagination,
        totalEntries: Math.max(0, pagination.totalEntries - suppressedCount),
      }

/**
 * Merge a newly fetched "load more" page into a section's existing state.
 *
 * Appends the page's visible tasks and carries the running suppressed count
 * forward, so the corrected `totalEntries` accounts for every page fetched so
 * far rather than only the newest one.
 */
export const appendSuppressedPage = (
  prev: SectionState,
  page: SectionData<TaskEntry>,
  options: SuppressionOptions
): Pick<SectionState, 'items' | 'pagination' | 'suppressedCount'> => {
  const { visibleItems, removedCount } = partitionSuppressedTasks(
    page.data || [],
    options
  )
  const suppressedCount = prev.suppressedCount + removedCount

  return {
    items: [...prev.items, ...visibleItems],
    pagination: withSuppressedTotal(page.pagination, suppressedCount),
    suppressedCount,
  }
}
